import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import Celebration from "../../components/Celebration.jsx";
import {
  ACTIONS,
  STARTING_LIVES,
  SCENARIOS_PER_LEVEL,
  pickScenario,
  timeLimitForLevel,
  computeGain,
} from "./data.js";
import "./MarketPulse.css";

const LEVEL_ID = "market-pulse";

const accentBorder = {
  cyan: "border-cyan text-cyan hover:bg-cyan hover:text-ink",
  magenta: "border-magenta text-magenta hover:bg-magenta hover:text-ink",
  gold: "border-gold text-gold hover:bg-gold hover:text-ink",
};

export default function MarketPulse() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();

  const [status, setStatus] = useState("intro"); // intro | playing | roundEnd | gameOver
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(STARTING_LIVES);
  const [combo, setCombo] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [feedback, setFeedback] = useState(null); // { correct: bool, action: id, timeout: bool }
  const [timeLimitMs, setTimeLimitMs] = useState(timeLimitForLevel(1));
  const [timeLeftPct, setTimeLeftPct] = useState(100);
  const [tickIndex, setTickIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const rafRef = useRef(null);
  const roundStartRef = useRef(0);
  const lockedRef = useRef(false);

  const stopTimer = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const spawnScenario = useCallback((lvl) => {
    const limit = timeLimitForLevel(lvl);
    setTimeLimitMs(limit);
    setCurrentScenario((prev) => pickScenario(prev?.id));
    setFeedback(null);
    lockedRef.current = false;
    roundStartRef.current = performance.now();
    setTimeLeftPct(100);
    setTickIndex(0);
  }, []);

  const handleTimeout = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    stopTimer();
    setFeedback({ correct: false, action: null, timeout: true });
    setCombo(0);
    setLives((l) => l - 1);
  }, [stopTimer]);

  // Countdown loop for the current scenario, driven by requestAnimationFrame
  // for a smooth timer bar. The cosmetic "ticking" price is derived from
  // this same clock (never a second setInterval) so there's only ever one
  // timing loop driving both the bar and the ticker.
  useEffect(() => {
    if (status !== "playing" || !currentScenario) return;

    const tick = (now) => {
      const elapsed = now - roundStartRef.current;
      const pct = Math.max(0, 100 - (elapsed / timeLimitMs) * 100);
      setTimeLeftPct(pct);

      const pathLen = currentScenario.pricePath.length;
      const idx = Math.min(pathLen - 1, Math.floor((elapsed / timeLimitMs) * pathLen));
      setTickIndex(idx);

      if (elapsed >= timeLimitMs) {
        handleTimeout();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => stopTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, currentScenario, timeLimitMs]);

  // Advance to next scenario / next level / game over after feedback shown.
  useEffect(() => {
    if (!feedback) return;

    const nextDelay = feedback.correct ? 900 : 1300;
    const timeout = setTimeout(() => {
      if (lives <= 0) {
        setStatus("gameOver");
        return;
      }

      const willLevelUp =
        correctCount > 0 && correctCount % SCENARIOS_PER_LEVEL === 0 && feedback.correct;
      const nextLevel = willLevelUp ? level + 1 : level;
      if (willLevelUp) setLevel(nextLevel);

      spawnScenario(nextLevel);
    }, nextDelay);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  useEffect(() => {
    if (lives <= 0 && status === "playing") {
      stopTimer();
      setStatus("gameOver");
    }
  }, [lives, status, stopTimer]);

  const startGame = () => {
    setLevel(1);
    setScore(0);
    setLives(STARTING_LIVES);
    setCombo(0);
    setCorrectCount(0);
    setSaved(false);
    setStatus("playing");
    spawnScenario(1);
  };

  const handleActionTap = (actionId) => {
    if (lockedRef.current || !currentScenario || status !== "playing") return;
    lockedRef.current = true;
    stopTimer();

    const correct = actionId === currentScenario.idealAction;

    if (correct) {
      const gained = computeGain(currentScenario.stakes, combo);
      setScore((s) => s + gained);
      setCombo((c) => c + 1);
      setCorrectCount((c) => c + 1);
    } else {
      setCombo(0);
      setLives((l) => l - 1);
    }

    setFeedback({ correct, action: actionId });
  };

  const didLevelUp = Math.floor(((profile?.xp ?? 0) + score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    if (status !== "gameOver") return undefined;
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      setSaving(true);

      const newXp = (profile?.xp ?? 0) + score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Market Pulse results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const currentPrice = currentScenario?.pricePath[tickIndex];
  const prevPrice =
    currentScenario && tickIndex > 0 ? currentScenario.pricePath[tickIndex - 1] : currentPrice;
  const tickClass =
    currentPrice > prevPrice ? "mp-tick-up" : currentPrice < prevPrice ? "mp-tick-down" : "";

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="panel relative overflow-hidden p-6">
        <Celebration show={saved} levelUp={didLevelUp} />
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="label-pixel text-gold">Level 13</p>
            <h1 className="pixel-heading text-base text-mist sm:text-xl">Market Pulse</h1>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: STARTING_LIVES }).map((_, i) => (
              <span key={i} className={`text-lg ${i < lives ? "opacity-100" : "opacity-20"}`}>
                ❤️
              </span>
            ))}
          </div>
        </div>

        {status === "intro" && (
          <div className="animate-popIn text-center">
            <p className="mb-2 text-sm text-mist/80">
              A ticker, a headline, and a moving price. Call it — <span className="text-cyan">Buy</span>,{" "}
              <span className="text-gold">Hold</span>, or <span className="text-magenta">Sell</span> —
              before the timer bar runs dry.
            </p>
            <p className="mb-6 text-xs text-mist/50">
              This isn't about predicting the market — every scenario has one disciplined right call.
              Speed increases every {SCENARIOS_PER_LEVEL} correct calls. Three wrong calls and it's
              Game Over.
            </p>
            <button onClick={startGame} className="btn-magenta">
              ▶ START GAME
            </button>
          </div>
        )}

        {status === "playing" && currentScenario && (
          <div>
            <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
              <span>SCORE {score.toLocaleString()}</span>
              <span>COMBO x{combo}</span>
              <span>LV.{level}</span>
            </div>

            <div className="mb-5 h-2 w-full border border-black bg-ink">
              <div
                className={`h-full transition-[width] duration-75 ${
                  timeLeftPct > 40 ? "bg-lime" : timeLeftPct > 15 ? "bg-gold" : "bg-danger"
                }`}
                style={{ width: `${timeLeftPct}%` }}
              />
            </div>

            <div
              key={currentScenario.id}
              className={`mp-card mb-4 border-2 border-black bg-ink px-4 py-5 ${
                feedback ? (feedback.correct ? "mp-correct" : "mp-wrong") : "animate-popIn"
              }`}
            >
              <div className="mb-2 flex items-baseline justify-between">
                <span className="font-display text-xs text-mist">${currentScenario.ticker}</span>
                <span key={tickIndex} className={`font-mono text-lg ${tickClass}`}>
                  ${currentPrice}
                </span>
              </div>
              <p className="text-sm text-mist/80">{currentScenario.headline}</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {ACTIONS.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleActionTap(action.id)}
                  className={`flex flex-col items-center gap-1 border-2 bg-transparent px-2 py-4 font-display text-[9px] uppercase transition-colors ${accentBorder[action.accent]}`}
                >
                  <span>{action.label}</span>
                  <span className="font-mono text-[8px] normal-case text-mist/50">{action.hint}</span>
                </button>
              ))}
            </div>

            {feedback && (
              <div className="mt-4 text-center">
                <p className={`font-display text-[10px] ${feedback.correct ? "text-lime" : "text-danger"}`}>
                  {feedback.timeout
                    ? "TOO SLOW!"
                    : feedback.correct
                    ? "+ NICE CALL!"
                    : `NOPE — SHOULD'VE BEEN ${currentScenario.idealAction}`}
                </p>
                <p className="mx-auto mt-2 max-w-sm text-xs text-mist/60">{currentScenario.rationale}</p>
              </div>
            )}
          </div>
        )}

        {status === "gameOver" && (
          <div className="animate-popIn text-center">
            <h2 className="pixel-heading mb-2 text-lg text-danger">GAME OVER</h2>
            <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">(Market Closed)</p>

            <div className="mx-auto mb-6 max-w-xs space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
              <div className="flex justify-between">
                <span className="text-mist/60">Final Score</span>
                <span className="text-gold">{score.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mist/60">Level Reached</span>
                <span className="text-cyan">{level}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mist/60">Good Calls</span>
                <span className="text-lime">{correctCount}</span>
              </div>
            </div>

            <p className="mb-6 text-xs text-mist/50">
              {saving
                ? "Saving to your profile…"
                : saved
                ? "Net worth and XP updated on your profile ✅"
                : "Results will save to your profile."}
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={startGame} className="btn-magenta">
                ↻ RETRY
              </button>
              <button onClick={() => navigate("/")} className="btn-cyan">
                LEVEL SELECT
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
